import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useIsWebView, useToken } from '../../../shared/hooks';
import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { appBridge } from '../../../shared/services/bridge';
import { usePaymentInfo } from '../hooks/usePaymentInfo';
import { paymentRepository } from '../data/paymentRepository';
import PaymentSummarySection from './PaymentSummarySection';

import './PaymentConfirm.css';
import './PaymentCommon.css';

// カード情報の型
interface CardInfo {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  securityCode: string;
  cardName: string;
}

// カード番号のマスク表示
const maskCardNumber = (cardNumber: string): string => {
  return cardNumber.replace(/(\d{4})/g, '$1 ').trim();
};

const PaymentConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isWebView = useIsWebView();
  const token = useToken();

  const cardInfo = location.state as CardInfo | undefined;
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // APIから決済情報を取得
  const { data: paymentData, isLoading, error } = usePaymentInfo(id || '', token);

  // 金額情報を計算（APIから price=税抜金額、taxRate、commissionRate を取得）
  const price = paymentData?.price || 0;
  const taxRate = paymentData?.taxRate || 0;
  const commissionRate = paymentData?.commissionRate || 0;
  // 事務手数料加算後に消費税を計算（ドクター側calcHasTaxと同じロジック）
  const commissionAmount = Math.floor(price * commissionRate / 100);
  const subtotalWithCommission = price + commissionAmount;
  const tax = Math.floor(subtotalWithCommission * taxRate / 100);
  const total = subtotalWithCommission + tax;

  // Flutter側のローディング制御
  const contentRef = useWebViewLoading(isLoading);

  // カード情報がない場合は入力画面に戻す
  if (!cardInfo && !isLoading) {
    navigate(`/inquiries/${id}/credit-card`, { replace: true });
    return null;
  }

  const handlePayment = async () => {
    if (!paymentData || !cardInfo || !id) return;

    setIsSubmitting(true);
    // 決済API実行中はFlutterローディングオーバーレイを表示
    appBridge.showLoading();

    try {
      const result = await paymentRepository.createPayment({
        inquiryId: id,
        paymentMethodCode: 'credit',
        price: paymentData.price,
        taxRate: paymentData.taxRate,
        creditInfo: {
          cardNumber: cardInfo.cardNumber.replace(/\s/g, ''),
          cardholderName: cardInfo.cardName,
          expiryMonth: parseInt(cardInfo.expiryMonth),
          expiryYear: parseInt(cardInfo.expiryYear),
          securityCode: cardInfo.securityCode,
        },
      }, token);

      if (result.success && result.data.redirect) {
        // GMOコールバック後にinquiry_idを復元できるようsessionStorageに保存
        if (id) {
          sessionStorage.setItem('payment_inquiry_id', id);
        }
        // GMOの3Dセキュア画面またはコールバックURLへリダイレクト
        window.location.href = result.data.redirect;
      } else {
        // エラー処理
        appBridge.hideLoading();
        navigate(`/inquiries/${id}/payment-failed`);
      }
    } catch {
      appBridge.hideLoading();
      navigate(`/inquiries/${id}/payment-failed`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // ローディング中はFlutter側のローディングが表示されるため、nullを返す
  if (isLoading) {
    return null;
  }

  // エラー時
  if (error || !paymentData) {
    return (
      <div ref={contentRef} className="confirm-container">
        <main className="confirm-content">
          <p>決済情報の取得に失敗しました。</p>
        </main>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="confirm-container">
      <main className="confirm-content">
        <h2 className="content-title">お支払い内容の確認</h2>

        {/* お支払い内容セクション（PPTX指示 0506 スライド12: 共通コンポーネント化） */}
        <PaymentSummarySection
          estimateGroups={paymentData?.estimateGroups}
          subtotalExcludingCommission={price}
          commissionAmount={commissionAmount}
          taxAmount={tax}
          totalAmount={total}
        />

        {/* カード内容セクション */}
        {cardInfo && (
          <section className="confirm-section">
            <h3 className="section-title">カード内容</h3>
            <div className="card-info-grid">
              <div className="card-info-item">
                <span className="card-info-label">カード名義人</span>
                <span className="card-info-value">{cardInfo.cardName}</span>
              </div>
              <div className="card-info-item">
                <span className="card-info-label">カード番号</span>
                <span className="card-info-value">{maskCardNumber(cardInfo.cardNumber)}</span>
              </div>
              <div className="card-info-item">
                <span className="card-info-label">MM / YY</span>
                <span className="card-info-value">{cardInfo.expiryMonth} / {cardInfo.expiryYear.slice(-2)}</span>
              </div>
              <div className="card-info-item">
                <span className="card-info-label">セキュリティコード</span>
                <span className="card-info-value">***</span>
              </div>
            </div>
            <div className="payment-count">
              <span className="payment-count-label">支払い回数</span>
              <span className="payment-count-value">1回</span>
            </div>
          </section>
        )}

        {/* 同意チェックボックス */}
        <div className="agreement-checkbox-container">
          <input
            type="checkbox"
            id="payment-agreement"
            checked={isAgreed}
            onChange={(e) => setIsAgreed(e.target.checked)}
          />
          <label htmlFor="payment-agreement">お支払い金額を確認しました</label>
        </div>
      </main>

      <footer className="confirm-footer">
        <button
          type="button"
          className="button-primary"
          onClick={handlePayment}
          disabled={!isAgreed || isSubmitting}
        >
          この内容で支払う
        </button>
        {!isWebView && (
          <button className="button-secondary" onClick={handleBack}>
            決済情報入力画面へ戻る
          </button>
        )}
      </footer>
    </div>
  );
};

export default PaymentConfirm;
